import { useState } from 'react';
import { ChevronUp, ChevronDown, CirclePlus, Info, AlertCircle, Redo2 } from 'lucide-react';
import { Button } from '@/components/base/Button';
import { cn } from '@/lib/utils/cn';
import type { AllergyConflictAlert, BetterMedicationSuggestion } from '@/lib/types/consultation';

/**
 * Alert Icon - Red exclamation for allergic/interaction warnings
 */
function AlertIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn('h-6 w-6', className)}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
        fill="#E85C5C"
      />
    </svg>
  );
}

/**
 * CDSS Medication Card Component
 *
 * Displays a medication suggestion card with warning and alternative sections,
 * matching the design shown in the image.
 */
export interface CDSSMedicationCardProps {
  /**
   * The medication suggestion data from CDSS. For cardType="allergy" this may
   * also carry the richer AllergyConflictAlert fields (allergy, severity,
   * source_phrase, evidence_confidence, timestamps).
   */
  suggestion?: BetterMedicationSuggestion & Partial<AllergyConflictAlert>;
  /**
   * Type of card - determines button behavior, labels, and badge styling
   * - 'better': Yellow badge "Better Medication" - shows Replace drug button
   * - 'alert': Red badge "Drug Interaction" - shows Replace drug button
   * - 'missed': Yellow badge "Missed Symptoms" - shows Add drug button only
   * - 'allergy': Red badge "Allergy" - shows Replace drug button
   */
  cardType?: 'alert' | 'missed' | 'better' | 'allergy';
  /**
   * Callback when "Add drug" button is clicked
   */
  onAddDrug?: () => void;
  /**
   * Callback when "Replace drug" button is clicked
   */
  onReplaceDrug?: () => void;
  /**
   * Additional className for the card container
   */
  className?: string;
}

/**
 * Validates if the replace_with value is a valid medicine name format
 */
function isValidReplaceWith(value?: string): boolean {
  if (!value) return false;

  const trimmed = value.trim();

  // Must have minimum length to be a real medicine name
  if (trimmed.length < 2) return false;

  // Must contain at least some letters (medicine names have letters)
  // This catches pure numbers, symbols, or single characters
  if (!/[a-zA-Z]/.test(trimmed)) return false;

  return true;
}

export function CDSSMedicationCard({
  suggestion,
  cardType,
  onAddDrug,
  onReplaceDrug,
  className,
}: CDSSMedicationCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Badge styling based solely on cardType (which maps to incoming object name)
  // Yellow badges: 'better' (betterMedicationSuggestions), 'missed' (missedSymptomAlerts)
  // Red badges: 'alert' (drugInteractionAlerts), 'allergy' (allergyConflictAlerts)
  const isAllergy = cardType === 'allergy';
  const isMissed = cardType === 'missed';
  const isInteraction = cardType === 'alert';
  const isBetter = cardType === 'better';

  // Check if replace_with is valid - but NOT for missed symptoms (they only use Add drug)
  const hasValidReplaceWith = !isMissed && isValidReplaceWith(suggestion?.replace_with);

  // Show red alert icon for allergy and drug interaction types
  const showAlertIcon = isAllergy || isInteraction;

  // Read-only contexts (e.g. Patient Record) pass neither callback — hide the
  // action button entirely rather than showing an Add/Replace drug button
  // that silently does nothing when clicked.
  const hasActions = Boolean(onAddDrug || onReplaceDrug);

  return (
    <div className={cn('rounded-xl border border-[#f8f9fb] bg-[rgba(234,235,243,0.5)]', className)}>
      <div className="flex flex-col gap-3 px-3 py-2">
        {/* Header: Medication Name and Expand Button */}
        <div className="flex h-6 items-center justify-between">
          <div className="flex items-center gap-[5px]">
            {/* Show alert icon for red flags (allergy, interaction) */}
            {showAlertIcon && <AlertIcon className="h-6 w-6" />}
            <h4 className="text-base font-semibold capitalize leading-6 text-[#051858]">
              {suggestion?.medication || 'Medication'}
            </h4>
          </div>
          <button
            type="button"
            className="flex h-4 w-4 items-center justify-center"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-[#1d1b20]" />
            ) : (
              <ChevronDown className="h-4 w-4 text-[#1d1b20]" />
            )}
          </button>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="flex flex-col gap-3">
            {/* Badges Row - Based on cardType */}
            <div className="flex flex-wrap gap-2">
              {/* Better Medication Badge - Yellow */}
              {isBetter && (
                <div className="flex items-center gap-[2px] rounded-2xl border border-[#bc8d04] bg-[#fff8e6] px-2 py-1">
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 1.33334C4.32 1.33334 1.33333 4.32001 1.33333 8.00001C1.33333 11.68 4.32 14.6667 8 14.6667C11.68 14.6667 14.6667 11.68 14.6667 8.00001C14.6667 4.32001 11.68 1.33334 8 1.33334ZM8 13.3333C5.06 13.3333 2.66667 10.94 2.66667 8.00001C2.66667 5.06001 5.06 2.66668 8 2.66668C10.94 2.66668 13.3333 5.06001 13.3333 8.00001C13.3333 10.94 10.94 13.3333 8 13.3333ZM8.66667 4.66668H7.33333V8.66668H8.66667V4.66668ZM8.66667 10H7.33333V11.3333H8.66667V10Z"
                      fill="#C99604"
                    />
                  </svg>
                  <span className="text-xs font-medium leading-4 text-[#bc8d04]">
                    Better Medication
                  </span>
                </div>
              )}

              {/* Drug Interaction Badge - Red */}
              {isInteraction && (
                <div className="flex items-center gap-[2px] rounded-2xl border border-[#d35454] bg-[#fff8f8] px-2 py-1">
                  <AlertCircle className="h-4 w-4 text-[#e85c5c]" />
                  <span className="text-xs font-medium leading-4 text-[#d35454]">
                    Drug Interaction
                  </span>
                </div>
              )}

              {/* Missed Symptoms Badge - Yellow */}
              {isMissed && (
                <div className="flex items-center gap-[2px] rounded-2xl border border-[#bc8d04] bg-[#fff8e6] px-2 py-1">
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 1.33334C4.32 1.33334 1.33333 4.32001 1.33333 8.00001C1.33333 11.68 4.32 14.6667 8 14.6667C11.68 14.6667 14.6667 11.68 14.6667 8.00001C14.6667 4.32001 11.68 1.33334 8 1.33334ZM8 13.3333C5.06 13.3333 2.66667 10.94 2.66667 8.00001C2.66667 5.06001 5.06 2.66668 8 2.66668C10.94 2.66668 13.3333 5.06001 13.3333 8.00001C13.3333 10.94 10.94 13.3333 8 13.3333ZM8.66667 4.66668H7.33333V8.66668H8.66667V4.66668ZM8.66667 10H7.33333V11.3333H8.66667V10Z"
                      fill="#C99604"
                    />
                  </svg>
                  <span className="text-xs font-medium leading-4 text-[#bc8d04]">
                    Missed Symptoms
                  </span>
                </div>
              )}

              {/* Allergy Badge - Red */}
              {isAllergy && (
                <div className="flex items-center gap-[2px] rounded-2xl border border-[#d35454] bg-[#fff8f8] px-2 py-1">
                  <AlertCircle className="h-4 w-4 text-[#e85c5c]" />
                  <span className="text-xs font-medium leading-4 text-[#e85c5c]">Allergy</span>
                </div>
              )}
            </div>

            {/* Allergy-specific details: the conflicting allergy, its severity, and the
                patient/physician quote + confidence the alert was derived from. */}
            {isAllergy && (suggestion?.allergy || suggestion?.severity) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#717381]">
                {suggestion?.allergy && (
                  <span>
                    Allergy:{' '}
                    <span className="font-medium text-[#42434b]">{suggestion.allergy}</span>
                  </span>
                )}
                {suggestion?.severity && (
                  <span>
                    Severity:{' '}
                    <span className="font-medium capitalize text-[#42434b]">
                      {suggestion.severity}
                    </span>
                  </span>
                )}
                {suggestion?.evidence_confidence && (
                  <span>
                    Confidence:{' '}
                    <span className="font-medium capitalize text-[#42434b]">
                      {suggestion.evidence_confidence}
                    </span>
                  </span>
                )}
              </div>
            )}
            {isAllergy && suggestion?.source_phrase && (
              <p className="text-xs italic leading-4 text-[#8c91aa]">
                “{suggestion.source_phrase}”
              </p>
            )}

            {/* Reason/Description with left border */}
            {suggestion?.explanation && (
              <div
                className={cn(
                  'border-l-[1.5px] border-solid pl-2',
                  isAllergy || isInteraction
                    ? 'border-l-[#e85c5c]'
                    : isMissed || isBetter
                      ? 'border-l-[#bc8d04]'
                      : 'border-l-[#6b8af9]'
                )}
              >
                <p className="text-sm font-normal leading-5 text-[#42434b]">
                  {suggestion.explanation}
                </p>
              </div>
            )}

            {/* Alternate Available Section - Only show for non-missed types with valid replace_with */}
            {hasValidReplaceWith && (
              <>
                {/* Alternate available badge - Blue */}
                <div className="flex w-fit items-center gap-[2px] rounded-2xl border border-[#6b8af9] bg-[#f8f9fb] px-2 py-1">
                  <Redo2 className="h-4 w-4 text-[#6b8af9]" />
                  <span className="text-xs font-medium leading-4 text-[#6b8af9]">
                    Alternate available
                  </span>
                </div>

                {/* Alternative medication with blue border */}
                <div className="flex items-end justify-between gap-1 border-l-[1.5px] border-solid border-l-[#6b8af9] pl-2">
                  <p className="flex-1 text-sm font-normal leading-5 text-[#42434b]">
                    {suggestion?.replace_with}
                  </p>
                  <Info className="h-4 w-4 shrink-0 cursor-pointer text-[#b3b3b3]" />
                </div>
              </>
            )}

            {/* Action Button — hidden entirely in read-only contexts (no callbacks provided) */}
            {hasActions && (
              <div className="flex justify-end">
                {/* For missed symptoms: only show Add drug button */}
                {isMissed ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 rounded-lg border border-[#f8f9fb] bg-white px-4 py-2 text-sm font-medium leading-5 text-[#526ec5] hover:bg-[#f8f9fb]"
                    onClick={() => onAddDrug?.()}
                  >
                    <CirclePlus className="mr-1 h-[18px] w-[18px]" />
                    Add drug
                  </Button>
                ) : (
                  /* For other types: show Replace drug if valid replace_with, otherwise Add drug */
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 rounded-lg border border-[#f8f9fb] bg-white px-4 py-2 text-sm font-medium leading-5 text-[#526ec5] hover:bg-[#f8f9fb]"
                    onClick={() => {
                      if (hasValidReplaceWith && onReplaceDrug) {
                        onReplaceDrug();
                      } else if (onAddDrug) {
                        onAddDrug();
                      }
                    }}
                  >
                    {hasValidReplaceWith ? (
                      <>
                        <Redo2 className="mr-1 h-[18px] w-[18px]" />
                        Replace drug
                      </>
                    ) : (
                      <>
                        <CirclePlus className="mr-1 h-[18px] w-[18px]" />
                        Add drug
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
